export const styles = /* css */ `
:host {
  all: initial;
  --la-accent: #c2410c;
  --la-ink: #1f2328;
  --la-muted: #6b7280;
  --la-surface: #ffffff;
  --la-line: rgba(15, 23, 42, 0.1);
  --la-shadow: 0 18px 50px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.08);
  font: 14px/1.45 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  color: var(--la-ink);
}
* { box-sizing: border-box; }
[hidden] { display: none !important; }

.root {
  position: fixed;
  right: 16px;
  bottom: 0;
  z-index: 2147483000;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  pointer-events: none;
}
.root > * { pointer-events: auto; }

/* Avatar ------------------------------------------------------------------ */
.stage { position: relative; width: 200px; height: 250px; flex: none; }
.avatar {
  all: unset;
  display: block;
  width: 100%;
  height: 100%;
  cursor: pointer;
  /* A light fade at the bottom edge so the cut-off body doesn't end abruptly. */
  -webkit-mask-image: linear-gradient(to bottom, #000 88%, rgba(0, 0, 0, 0.7) 100%);
          mask-image: linear-gradient(to bottom, #000 88%, rgba(0, 0, 0, 0.7) 100%);
  transition: transform 0.25s ease;
}
.avatar:hover { transform: translateY(-3px); }
.avatar:focus-visible { outline: 3px solid var(--la-accent); outline-offset: -3px; border-radius: 16px; }
.avatar canvas { width: 100%; height: 100%; display: block; filter: drop-shadow(0 10px 18px rgba(0,0,0,0.22)); }

.status {
  position: absolute;
  left: 50%;
  bottom: 38px;
  transform: translateX(-50%);
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.82);
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
}
.status::before {
  content: "";
  width: 7px; height: 7px; border-radius: 50%;
  background: #22c55e;
  animation: la-pulse 1.2s ease-in-out infinite;
}
.status[data-state="thinking"]::before { background: #f59e0b; }
.status[data-state="listening"]::before { background: #ef4444; }
@keyframes la-pulse { 50% { opacity: 0.35; } }

.controls { position: absolute; top: 6px; right: 4px; display: grid; gap: 6px; }
.ctl {
  all: unset;
  width: 28px; height: 28px;
  border-radius: 50%;
  display: grid; place-items: center;
  background: rgba(255,255,255,0.95);
  color: var(--la-ink);
  box-shadow: 0 2px 8px rgba(0,0,0,0.18);
  cursor: pointer;
}
.ctl:hover { background: #fff; color: var(--la-accent); }
.ctl:focus-visible { outline: 2px solid var(--la-accent); outline-offset: 2px; }
.ctl svg { width: 15px; height: 15px; }
.ctl.mute { opacity: 0; transition: opacity 0.2s; }
.stage:hover .ctl.mute, .ctl.mute:focus-visible, .ctl.mute[aria-pressed="true"] { opacity: 1; }
@media (hover: none) { .ctl.mute { opacity: 1; } }

/* Guide switcher (several guides on one page) */
.switcher {
  position: absolute;
  bottom: calc(100% + 6px);
  right: 0;
  display: flex;
  gap: 4px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(255,255,255,0.96);
  box-shadow: 0 6px 20px rgba(15,23,42,0.16);
  white-space: nowrap;
}
.root.left .switcher { right: auto; left: 0; }
.switch {
  all: unset;
  display: flex; align-items: center; gap: 6px;
  padding: 3px 10px 3px 3px;
  border-radius: 999px;
  font-size: 12px; font-weight: 600;
  color: var(--la-muted);
  cursor: pointer;
}
.switch:hover { color: var(--la-ink); background: rgba(15,23,42,0.05); }
.switch[aria-pressed="true"] { background: var(--la-accent); color: #fff; }
.switch:focus-visible { outline: 2px solid var(--la-accent); outline-offset: 1px; }
.switch .face { width: 30px; height: 30px; border-radius: 50%; background-color: #eef0ff; background-repeat: no-repeat; flex: none; box-shadow: inset 0 0 0 2px rgba(255,255,255,0.9); }
.switch .label { max-width: 110px; overflow: hidden; text-overflow: ellipsis; }

/* Minimized: a small face in the corner */
.mini {
  all: unset;
  pointer-events: auto; /* all: unset would inherit the root's pointer-events: none */
  position: relative;
  width: 60px; height: 60px;
  margin: 0 0 16px;
  border-radius: 50%;
  box-shadow: 0 0 0 3px #fff, 0 8px 24px rgba(15,23,42,0.25);
  cursor: pointer;
  transition: transform 0.2s ease;
}
.mini-face {
  position: absolute; inset: 0;
  border-radius: 50%;
  background-color: #eef0ff;
  background-repeat: no-repeat;
}
.mini.branded { width: 76px; height: 76px; }
.mini.branded .mini-face { inset: 10px; width: 56px; height: 56px; box-shadow: 0 0 0 2px #fff; }
.mini-ring { position: absolute; inset: 0; width: 100%; height: 100%; }
.mini-ring text { font: 800 9.5px/1 system-ui, -apple-system, "Segoe UI", sans-serif; letter-spacing: 2.4px; fill: #fff; }

/* "mugam.site" badge at the base of the avatar, clear of the face */
.badge {
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  display: inline-flex; align-items: center;
  padding: 3px 9px 3px 10px;
  border-radius: 999px;
  background: rgba(255,255,255,0.9);
  box-shadow: 0 2px 10px rgba(15,23,42,0.18);
  font: 800 11px/1.2 system-ui, -apple-system, "Segoe UI", sans-serif;
  letter-spacing: -0.01em;
  color: #16161d;
  text-decoration: none;
  white-space: nowrap;
}
.badge img { width: 12px; height: 12px; margin: 0 1px 0 2px; position: relative; top: 1px; }
.badge:hover { background: #fff; }
.badge:focus-visible { outline: 2px solid var(--la-accent); outline-offset: 2px; }
.mini:hover { transform: translateY(-2px) scale(1.04); }
.mini:focus-visible { outline: 3px solid var(--la-accent); outline-offset: 3px; }

/* Speech bubble ------------------------------------------------------------ */
.bubble {
  position: absolute;
  right: 165px;
  bottom: 70px;
  width: max-content;
  max-width: min(300px, calc(100vw - 200px));
  padding: 12px 14px;
  border-radius: 16px 16px 4px 16px;
  background: var(--la-surface);
  box-shadow: var(--la-shadow);
  cursor: pointer;
  animation: la-pop 0.25s ease-out;
}
.bubble small { display: block; margin-top: 6px; color: var(--la-accent); font-weight: 600; }
@keyframes la-pop { from { opacity: 0; transform: translateY(6px) scale(0.97); } }

/* Chat panel ---------------------------------------------------------------- */
.panel {
  width: min(360px, calc(100vw - 32px));
  height: min(520px, calc(100vh - 40px));
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  background: var(--la-surface);
  box-shadow: var(--la-shadow);
  overflow: hidden;
  animation: la-pop 0.25s ease-out;
}
header {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--la-line);
}
header strong { display: block; font-size: 15px; }
header span { color: var(--la-muted); font-size: 12px; }
.header-actions { display: flex; align-items: center; gap: 4px; }
.show-suggestions { all: unset; color: var(--la-accent); font-size: 11px; cursor: pointer; padding: 6px; border-radius: 6px; }
.show-suggestions:hover { background: rgba(15,23,42,0.06); }
.show-suggestions:focus-visible { outline: 2px solid var(--la-accent); }
.icon-btn {
  all: unset;
  width: 32px; height: 32px;
  border-radius: 8px;
  display: grid; place-items: center;
  cursor: pointer;
  color: var(--la-muted);
}
.icon-btn:hover { background: rgba(15,23,42,0.06); color: var(--la-ink); }
.icon-btn:focus-visible { outline: 2px solid var(--la-accent); }
.icon-btn svg { width: 18px; height: 18px; }

.log { flex: 1; min-height: 64px; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 10px; }
.msg { max-width: 85%; padding: 9px 12px; border-radius: 14px; white-space: pre-wrap; word-wrap: break-word; }
.msg.assistant { align-self: flex-start; background: #f3f4f6; border-bottom-left-radius: 4px; }
.msg.user { align-self: flex-end; background: var(--la-accent); color: #fff; border-bottom-right-radius: 4px; }
.msg.error { background: #fef2f2; color: #991b1b; }
.msg .go { display: inline-block; margin-top: 6px; color: var(--la-accent); font-weight: 600; text-decoration: none; }
.msg .go:hover { text-decoration: underline; }

/* Owner's follow-up after an answer: a link button or a short form */
.action { align-self: flex-start; max-width: 85%; }
.action-link {
  display: inline-block; padding: 8px 14px; border-radius: 999px; background: var(--la-accent); color: #fff;
  font-weight: 600; font-size: 14px; text-decoration: none;
}
.action-link:hover { filter: brightness(1.08); }
.action-link:focus-visible { outline: 2px solid var(--la-accent); outline-offset: 2px; }
form.lead {
  display: grid; gap: 4px; padding: 12px; border: 1px solid var(--la-line); border-radius: 14px;
  border-bottom-left-radius: 4px; background: #fff; width: 260px; max-width: 100%;
}
.lead-title { font-weight: 600; margin-bottom: 4px; }
.lead label { font-size: 12px; color: var(--la-muted); margin-top: 4px; }
.lead input, .lead textarea {
  all: unset; box-sizing: border-box; width: 100%; padding: 7px 10px; border: 1px solid var(--la-line);
  border-radius: 8px; font-size: 14px; background: #fff;
}
.lead textarea { white-space: pre-wrap; min-height: 60px; }
.lead input:focus, .lead textarea:focus { border-color: var(--la-accent); }
.lead .trap { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; }
.lead-send {
  all: unset; margin-top: 8px; padding: 8px 14px; border-radius: 10px; background: var(--la-accent); color: #fff;
  font-weight: 600; font-size: 14px; text-align: center; cursor: pointer;
}
.lead-send:hover { filter: brightness(1.08); }
.lead-send:focus-visible { outline: 2px solid var(--la-accent); outline-offset: 2px; }
.lead-send[disabled] { opacity: 0.6; cursor: default; }
.lead-status { font-size: 12px; color: #991b1b; min-height: 0; }
.lead-status:empty { display: none; }
.lead-note { font-size: 11px; color: var(--la-muted); }
.lead-done { padding: 9px 12px; border-radius: 14px; border-bottom-left-radius: 4px; background: #ecfdf5; color: #065f46; }
.typing { display: inline-flex; gap: 4px; }
.typing i { width: 6px; height: 6px; border-radius: 50%; background: var(--la-muted); animation: la-pulse 1s infinite; }
.typing i:nth-child(2) { animation-delay: 0.15s; }
.typing i:nth-child(3) { animation-delay: 0.3s; }

.suggestions { flex: none; border-top: 1px solid var(--la-line); padding: 10px 14px; background: #fafaff; }
.suggestion-header { display: flex; align-items: center; justify-content: space-between; margin: -4px -4px 4px 0; }
.suggestion-label { color: var(--la-muted); font-size: 11px; font-weight: 600; }
.suggestion-close { width: 28px; height: 28px; }
.suggestion-close svg { width: 14px; height: 14px; }
.suggestion-empty { color: var(--la-muted); font-size: 12px; }
.chips { display: flex; flex-wrap: wrap; gap: 6px; max-height: 158px; overflow-y: auto; }
.suggestions[data-started="true"] .chips { max-height: 100px; }
.chip {
  all: unset;
  padding: 6px 11px;
  border: 1px solid var(--la-line);
  border-radius: 999px;
  font-size: 13px;
  line-height: 1.35;
  max-width: 100%;
  overflow-wrap: anywhere;
  cursor: pointer;
}
.chip:hover { border-color: var(--la-accent); color: var(--la-accent); }
.chip:focus-visible { outline: 2px solid var(--la-accent); }

form { flex: none; display: flex; gap: 6px; padding: 10px 12px 12px; border-top: 1px solid var(--la-line); }
input {
  all: unset;
  flex: 1;
  min-width: 0;
  padding: 9px 12px;
  border: 1px solid var(--la-line);
  border-radius: 10px;
  font-size: 14px;
}
input:focus { border-color: var(--la-accent); }
.send { background: var(--la-accent); color: #fff; }
.send:hover { background: var(--la-accent); color: #fff; filter: brightness(1.08); }
.mic[aria-pressed="true"] { background: #fee2e2; color: #dc2626; }

/* Bottom-left placement (data-position="left"), e.g. two guides on one page */
.root.left { right: auto; left: 16px; flex-direction: row-reverse; }
.root.left .bubble { right: auto; left: 165px; border-radius: 16px 16px 16px 4px; }

@media (max-width: 560px) {
  /* Lift clear of mobile browser toolbars (iOS Safari's bottom address bar covers fixed bottom: 0). */
  .root {
    flex-direction: column-reverse; align-items: flex-end; right: 8px;
    bottom: max(64px, calc(env(safe-area-inset-bottom, 0px) + 28px));
  }
  .root.left { left: 8px; align-items: flex-start; }
  .root.left .bubble { left: 102px; }
  .stage { width: 128px; height: 160px; }
  .panel { margin-bottom: 0; height: min(460px, calc(100vh - 300px)); height: min(460px, calc(100dvh - 300px)); }
  .bubble { right: 102px; bottom: 44px; max-width: calc(100vw - 130px); }
  .log { min-height: 48px; }
  .chips { max-height: clamp(40px, calc(100dvh - 580px), 124px); }
  .suggestions[data-started="true"] .chips { max-height: clamp(40px, calc(100dvh - 580px), 80px); }
}
@media (prefers-reduced-motion: reduce) {
  .bubble, .panel { animation: none; }
}
`;
